import React, { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react'

import { isMobile } from '@/core/lib'

type TouchContextType = {
  isTouching: boolean
  setIsTouching: (v: boolean) => void
}

const TouchContext = createContext<TouchContextType>({
  isTouching: false,
  setIsTouching: () => {},
})

export const TouchProvider = ({
  children,
  containerRef,
}: {
  children: ReactNode
  containerRef: React.RefObject<HTMLDivElement>
}) => {
  const [isTouching, setIsTouching] = useState(false)
  const isUserScrolling = useRef(false)
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null)
  const isTouchingRef = useRef(false)
  const touchTimeout = useRef<NodeJS.Timeout | null>(null)

  // 실제 유저의 터치/휠로 인한 스크롤만 감지
  useEffect(() => {
    if (isMobile() && containerRef.current) {
      const handleTouchStart = () => {
        if (touchTimeout.current) clearTimeout(touchTimeout.current)
        setIsTouching(() => true)
        isTouchingRef.current = true
      }
      const handleTouchEnd = () => {
        if (touchTimeout.current) clearTimeout(touchTimeout.current)
        touchTimeout.current = setTimeout(() => {
          setIsTouching(() => false)
          isTouchingRef.current = false
        }, 200)
      }

      containerRef.current?.addEventListener('pointerdown', handleTouchStart, { passive: false })
      containerRef.current?.addEventListener('touchend', handleTouchEnd, { passive: false })
      containerRef.current?.addEventListener('pointerup', handleTouchEnd, { passive: false })
      return () => {
        containerRef.current?.removeEventListener('pointerdown', handleTouchStart)
        containerRef.current?.removeEventListener('touchend', handleTouchEnd)
        containerRef.current?.removeEventListener('pointerup', handleTouchEnd)
      }
    } else {
      // wheel 이벤트: 마우스 휠로 인한 스크롤
      const handleWheel = () => {
        if (!isUserScrolling.current) {
          isUserScrolling.current = true
          setIsTouching(true)
          isTouchingRef.current = true
        }
        if (scrollTimeout.current) clearTimeout(scrollTimeout.current)
        scrollTimeout.current = setTimeout(() => {
          isUserScrolling.current = false
          setIsTouching(false)
          isTouchingRef.current = false
        }, 300)
      }
      window.addEventListener('wheel', handleWheel, { passive: true })
      return () => {
        window.removeEventListener('wheel', handleWheel)
      }
    }
  }, [containerRef.current])

  return (
    <TouchContext.Provider value={{ isTouching, setIsTouching }}>
      <div id="touch-container">{children}</div>
    </TouchContext.Provider>
  )
}

export const useTouch = () => useContext(TouchContext)
